/**
 * The five primary sections (D-004). Today is the default landing route.
 *
 * Order here is the order of the bottom tab bar and must match
 * docs/ui-ux-spec.md.
 */
export interface RouteDefinition {
  /** Hash path, e.g. `/today` — rendered as `#/today`. */
  readonly path: string
  /** Tab label. Text labels are used rather than icons alone so state is never
      communicated by colour or glyph alone. */
  readonly label: string
  /** Large navigation title for the screen. */
  readonly title: string
}

export const ROUTES = [
  { path: '/today', label: 'Today', title: 'Today' },
  { path: '/progress', label: 'Progress', title: 'Progress' },
  { path: '/review', label: 'Review', title: 'Weekly Review' },
  { path: '/photos', label: 'Photos', title: 'Photos' },
  { path: '/settings', label: 'Settings', title: 'Settings' },
] as const satisfies readonly RouteDefinition[]

export const DEFAULT_ROUTE = '/today'

/**
 * First-run Project Setup (D-062, D-063). Deliberately not a tab — it is a
 * one-time task, and showing tabs during it invites the user to wander into
 * screens that cannot work yet.
 *
 * The path is unchanged from the three-step onboarding it replaced, so a
 * bookmark or a deferred update that pointed at it still lands somewhere real.
 */
export const ONBOARDING_ROUTE = '/onboarding'

/**
 * My Plan (D-090, D-099). A full screen, and deliberately **not** a sixth tab.
 *
 * The plan needs more room than a disclosure on Today can give it, and Today has
 * to stay the execution surface — one dominant action, a stable layout, nothing
 * competing. So it is a route reached from Today and from the empty states that
 * refer to it, with Back returning where the user came from.
 *
 * **The tab bar stays visible here**, unlike Project Setup. Setup suppresses it
 * because it is a one-way task where wandering off leads to screens that cannot
 * work yet; the plan is an ordinary screen the user is reading, and trapping them
 * on it would be worse than showing a tab bar with nothing current in it.
 *
 * D-004's five-section structure is untouched: five tabs, one extra destination.
 */
export const PLAN_ROUTE = '/plan'

/**
 * My Plan, opened from an accepted second opinion (D-093 §6, C7.0 phase 5).
 *
 * **A route parameter rather than a query string**, because the app is
 * hash-routed: `useSearch` reads the document's real search, not the one inside
 * the hash, so a `?` here would be read by nothing.
 *
 * **The proposal is named by id and re-read from storage rather than carried in
 * the URL.** The values a user is about to save must come from the record the app
 * kept, not from a string anything could have written — and the proposal was
 * already stored at phase 4, so there is nothing to invent.
 *
 * Landing here **pre-fills and writes nothing**. Revisiting the URL later
 * re-opens the same editor with the same numbers, which is harmless for the same
 * reason: saving is still a separate, explicit action.
 */
export const PLAN_FROM_PROPOSAL_ROUTE = '/plan/from/:proposalId'

export function planFromProposalPath(proposalId: string): string {
  return `/plan/from/${proposalId}`
}

/**
 * Choosing the phase that follows a finished one (D-103 §3).
 *
 * **A route rather than a section in Settings**, for the same reason Project
 * Setup is one: it is a focused task with a measurement protocol in the middle
 * of it, and D-020 requires that reaching the end changes nothing until an
 * explicit confirmation. A form that long inside a Settings disclosure would be
 * a decision buried under an accordion.
 *
 * **The tab bar is suppressed here, as it is during setup.** The flow re-takes a
 * measurement and re-runs the route engine; wandering off mid-way and coming
 * back to a half-entered set of readings is the same hazard setup suppresses the
 * tabs to avoid. Leaving is a labelled control, not an accident.
 *
 * D-004's five-section structure is untouched: five tabs, three extra
 * destinations.
 */
export const NEXT_PHASE_ROUTE = '/next-phase'
